import { useEffect, useState } from "react";
import { Send, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export const EmailsTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [ctaText, setCtaText] = useState("Shop Now");
  const [ctaPath, setCtaPath] = useState("/shop");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/admin/subscribers").then(({ data }) => setSubscribers(data)).catch(() => {});
    api.get("/admin/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this campaign to ${subscribers.length} subscriber(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns", {
        subject, heading, body, cta_text: ctaText, cta_path: ctaPath,
      });
      toast.success(`Campaign sent to ${data.sent_count} subscriber(s)`);
      setSubject(""); setHeading(""); setBody("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "mt-1 w-full bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm transition-colors";

  return (
    <div data-testid="emails-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight uppercase">Email Marketing</h2>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#E29D47] border border-[#2C2623] px-4 py-2" data-testid="subscriber-count">
          <Users className="w-4 h-4" /> {subscribers.length} subscriber(s)
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <form onSubmit={send} className="space-y-4 bg-[#171412] border border-[#2C2623] p-6" data-testid="campaign-form">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36]">New campaign</p>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Subject line</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="THE ESPRESSO DROP IS LIVE" className={inputCls} data-testid="campaign-subject-input" />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Heading</span>
            <input value={heading} onChange={(e) => setHeading(e.target.value)} required placeholder="New heat just landed" className={inputCls} data-testid="campaign-heading-input" />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Body</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={5} placeholder="Tell your rebels what's dropping, which code to use, and why they should move fast." className={`${inputCls} resize-none`} data-testid="campaign-body-input" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Button text</span>
              <input value={ctaText} onChange={(e) => setCtaText(e.target.value)} className={inputCls} data-testid="campaign-cta-text-input" />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Button link (path)</span>
              <input value={ctaPath} onChange={(e) => setCtaPath(e.target.value)} placeholder="/shop or /promo/REBEL15" className={inputCls} data-testid="campaign-cta-path-input" />
            </label>
          </div>
          <button
            type="submit"
            disabled={sending || subscribers.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider py-3.5 hover:bg-[#F06B47] active:scale-[0.98] transition-all disabled:opacity-40"
            data-testid="campaign-send-button"
          >
            <Send className="w-4 h-4" /> {sending ? "Sending..." : `Send to ${subscribers.length} subscriber(s)`}
          </button>
          {subscribers.length === 0 && (
            <p className="text-[11px] font-mono text-[#7A7067] uppercase tracking-widest text-center">
              No subscribers yet — they join via the footer form on your store
            </p>
          )}
        </form>

        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36] mb-4">Live preview</p>
          <div className="bg-[#0C0A09] border border-[#2C2623] p-4" data-testid="campaign-preview">
            <div className="bg-[#171412] border border-[#2C2623] p-6 max-w-md mx-auto">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#E05A36] mb-3">Popular Rebelz</p>
              <h3 className="font-display font-bold text-lg uppercase tracking-tight mb-3">{heading || "Your heading here"}</h3>
              <p className="text-sm text-[#B8AEA5] leading-relaxed whitespace-pre-line mb-5">{body || "Your message preview appears here as you type."}</p>
              <span className="inline-block bg-[#E05A36] text-[#0C0A09] font-bold uppercase text-xs tracking-wider px-5 py-3">{ctaText || "Shop Now"}</span>
              <p className="text-[10px] text-[#7A7067] mt-5">Sent by Popular Rebelz. We never ask for your password or card details by email.</p>
            </div>
          </div>
        </div>
      </div>

      <h3 className="font-display font-bold uppercase tracking-tight mb-4">Past campaigns</h3>
      <div className="space-y-2" data-testid="campaign-history">
        {campaigns.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 bg-[#171412] border border-[#2C2623] px-4 py-3 text-sm" data-testid={`campaign-row-${c.id}`}>
            <div>
              <p className="font-bold">{c.subject}</p>
              <p className="text-[10px] font-mono text-[#7A7067] uppercase tracking-widest">{new Date(c.created_at).toLocaleString()}</p>
            </div>
            <span className="text-xs font-mono text-[#E29D47]">{c.sent_count} sent</span>
          </div>
        ))}
        {campaigns.length === 0 && (
          <p className="text-center text-[#7A7067] font-mono text-xs uppercase tracking-widest py-8 border border-[#2C2623]">No campaigns sent yet</p>
        )}
      </div>
    </div>
  );
};
